import React, { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Play, CheckCircle2, XCircle, AlertTriangle, Clock, Activity,
  Shield, Settings, FileText, Grid3X3, Presentation, LayoutGrid,
  BookOpen, Bot, Monitor, Layers, RefreshCw, Loader2, ChevronDown, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

const ICONS = {
  Shield, Settings, FileText, Grid3x3: Grid3X3, Presentation, LayoutGrid,
  BookOpen, Bot, Monitor, Layers
};

function StatusDot({ status }) {
  if (status === 'pass') return <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />;
  if (status === 'fail') return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
  return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
}

function ModuleCard({ module, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const passed = module.tests.filter(t => t.status === 'pass').length;
  const total = module.tests.length;
  const allPass = passed === total;
  const Icon = ICONS[module.icon] || Activity;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${allPass ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'}`} data-testid={`module-${module.module.toLowerCase().replace(/\s+/g, '-')}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors">
        <Icon className={`w-5 h-5 shrink-0 ${allPass ? 'text-green-500' : 'text-red-500'}`} />
        <span className="text-sm font-semibold flex-1 text-left">{module.module}</span>
        <Badge variant={allPass ? 'default' : 'destructive'} className={`text-[10px] ${allPass ? 'bg-green-500/20 text-green-600 hover:bg-green-500/30' : ''}`}>
          {passed}/{total}
        </Badge>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border/50">
          {module.tests.map((test, i) => (
            <div key={i} className={`px-4 py-2 flex items-center gap-3 text-xs ${i > 0 ? 'border-t border-border/30' : ''} ${test.status !== 'pass' ? 'bg-red-500/5' : ''}`}>
              <StatusDot status={test.status} />
              <span className="flex-1 font-medium">{test.name}</span>
              <span className="text-muted-foreground font-mono">{test.time_ms}ms</span>
              <span className={`font-mono ${test.code === test.expected || (Array.isArray(test.expected) && test.expected.includes(test.code)) ? 'text-green-500' : 'text-red-500'}`}>
                {test.code}
              </span>
              {test.error && (
                <span className="text-red-400 truncate max-w-[200px]" title={test.error}>{test.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const HealthCheck = () => {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/admin/health-check/history`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setHistory(d.results || []))
      .catch(() => {});
  }, [result]);

  const runTests = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/admin/health-check/run`, {
        method: 'POST', headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setResult(data);
      toast.success(`Health check complete: ${data.passed}/${data.total} passed`);
    } catch (e) {
      toast.error('Health check failed to run');
    } finally {
      setRunning(false);
    }
  };

  const displayResult = result || (history.length > 0 ? history[0] : null);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="health-check-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Activity className="w-7 h-7 text-primary" />
            Platform Health Check
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Run comprehensive regression tests across all {displayResult?.total || 63} API endpoints
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="history-btn">
            <Clock className="w-4 h-4 mr-1" /> History
          </Button>
          <Button onClick={runTests} disabled={running} size="lg" data-testid="run-tests-btn">
            {running ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Running Tests...</> : <><Play className="w-5 h-5 mr-2" />Run All Tests</>}
          </Button>
        </div>
      </div>

      {/* Running state */}
      {running && (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
          <h2 className="text-lg font-semibold mb-1">Running Regression Tests...</h2>
          <p className="text-sm text-muted-foreground">Testing all modules: Auth, Admin, Suite, Study Material, Bot Exam, Classroom, Labs...</p>
        </div>
      )}

      {/* Summary Cards */}
      {displayResult && !running && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            <SummaryCard label="Total Tests" value={displayResult.total} icon={Activity} color="text-primary" />
            <SummaryCard label="Passed" value={displayResult.passed} icon={CheckCircle2} color="text-green-500" />
            <SummaryCard label="Failed" value={displayResult.failed} icon={XCircle} color="text-red-500" />
            <SummaryCard label="Errors" value={displayResult.errors} icon={AlertTriangle} color="text-amber-500" />
            <SummaryCard label="Avg Response" value={`${displayResult.avg_response_ms}ms`} icon={Clock} color="text-blue-500" />
          </div>

          {/* Pass Rate Bar */}
          <div className="mb-8 p-4 rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Overall Pass Rate</span>
              <span className={`text-2xl font-bold ${displayResult.pass_rate >= 95 ? 'text-green-500' : displayResult.pass_rate >= 80 ? 'text-amber-500' : 'text-red-500'}`}>
                {displayResult.pass_rate}%
              </span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-1000 ${displayResult.pass_rate >= 95 ? 'bg-green-500' : displayResult.pass_rate >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${displayResult.pass_rate}%` }} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Last run: {new Date(displayResult.run_at).toLocaleString()} | {displayResult.modules?.length || 0} modules tested
            </p>
          </div>

          {/* Module Results */}
          <div className="space-y-3">
            {(displayResult.modules || []).map((mod, i) => (
              <ModuleCard key={i} module={mod} defaultOpen={mod.tests.some(t => t.status !== 'pass')} />
            ))}
          </div>
        </>
      )}

      {/* No data state */}
      {!displayResult && !running && (
        <div className="text-center py-20">
          <Activity className="w-16 h-16 mx-auto text-muted-foreground/20 mb-4" />
          <h2 className="text-lg font-semibold mb-2">No test results yet</h2>
          <p className="text-sm text-muted-foreground mb-6">Run the health check to test all platform functionality</p>
          <Button onClick={runTests}><Play className="w-4 h-4 mr-1" /> Run First Health Check</Button>
        </div>
      )}

      {/* History Panel */}
      {showHistory && history.length > 0 && (
        <div className="mt-8 p-4 rounded-xl border border-border bg-card">
          <h3 className="text-sm font-semibold mb-3">Test History</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50" onClick={() => { setResult(h); setShowHistory(false); }}>
                <div className="flex items-center gap-2">
                  {h.pass_rate >= 95 ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                  <span className="text-xs font-medium">{new Date(h.run_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">{h.passed}/{h.total} passed</span>
                  <Badge variant={h.pass_rate >= 95 ? 'default' : 'destructive'} className={`text-[10px] ${h.pass_rate >= 95 ? 'bg-green-500/20 text-green-600' : ''}`}>{h.pass_rate}%</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <div className="p-3 rounded-xl border border-border bg-card text-center">
      <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
      <p className="text-xl font-bold">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
